//
// Copyright (c) 2011-2020 by Curalate, Inc.
//

import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { Auth } from 'aws-amplify';

import { EMPLOYEE_VIEW_OPTIONS } from '@account-settings/accountSettingsConstants';
import { IAccountSettingsState } from '@account-settings/redux/accountSettings/accountSettingsModel';
import { getTrackingActionForEmployeeView } from '@account-settings/redux/accountSettings/accountSettingsUtils';
import * as accountSettingsApi from '@apis/account-settings/accountSettingsApi';
import {
  IGetElevationResponse,
  ITimeZone,
} from '@apis/account-settings/accountSettingsApiModel';
import * as gatewayAuthenticationApi from '@apis/auth/gatewayAuthenticationApi';
import AuthType from '@apis/auth/models/AuthType';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import TimeZoneId from '@models/TimeZoneId';
import { SliceNames } from '@redux/SliceNames';
import { createThunk } from '@redux/createThunk';
import { recordEvent } from '@tracker/tracker';
import {
  TRACKING_ACTION,
  TRACKING_CATEGORY,
  TRACKING_LABEL,
} from '@tracker/trackerModel';
import { isNotNull } from '@utils/filterPredicates';
import { getRequestErrorMessage } from '@utils/getRequestErrorMessage';

const initialState: IAccountSettingsState = {
  timezones: undefined,
  userAuthType: undefined,
  currentView: undefined,
  isElevated: false,
  elevationExpirationTimestamp: undefined,
  flags: {
    isRequestingTimezones: false,
    isRequestingSetTimezone: false,
    isRequestingUserAuthType: false,
    isRequestingChangePassword: false,
    isRequestingUserView: false,
    isRequestingSetUserView: false,
    isRequestingUserElevation: false,
    isRequestingRevokeUserElevation: false,
    isRequestingElevateUser: false,
  },
  errors: {
    requestTimezonesError: undefined,
    setTimezoneError: undefined,
    requestUserAuthTypeError: undefined,
    changePasswordError: undefined,
    userViewError: undefined,
    setUserViewError: undefined,
    userElevationError: undefined,
    revokeUserElevationError: undefined,
    elevateUserError: undefined,
  },
};

const { actions, reducer } = createSlice({
  name: SliceNames.AccountSettingsAccount,
  initialState,
  reducers: {
    requestTimezones(state) {
      state.flags.isRequestingTimezones = true;
    },
    receiveTimezonesSucceeded(state, action: PayloadAction<ITimeZone[]>) {
      state.timezones = action.payload;
      state.flags.isRequestingTimezones = false;
    },
    receiveSetTimezoneFailed(state, action: PayloadAction<string>) {
      state.errors.setTimezoneError = action.payload;
      state.flags.isRequestingTimezones = false;
    },
    requestSetTimezone(state) {
      state.flags.isRequestingSetTimezone = true;
    },
    receiveSetTimezoneSucceeded(state) {
      state.flags.isRequestingSetTimezone = false;
    },
    receiveTimezonesFailed(state, action: PayloadAction<string>) {
      state.errors.requestTimezonesError = action.payload;
      state.flags.isRequestingSetTimezone = false;
    },
    requestUserAuthType(state) {
      state.flags.isRequestingUserAuthType = true;
    },
    receiveUserAuthTypeSucceeded(
      state,
      action: PayloadAction<AuthType | null>,
    ) {
      if (isNotNull(action.payload)) {
        state.userAuthType = action.payload;
      }
      state.flags.isRequestingUserAuthType = false;
    },
    receiveUserAuthTypeFailed(state, action: PayloadAction<string>) {
      state.errors.requestUserAuthTypeError = action.payload;
      state.flags.isRequestingUserAuthType = false;
    },
    requestChangePassword(state) {
      state.flags.isRequestingChangePassword = true;
      state.errors.changePasswordError = undefined;
    },
    receiveChangePasswordSucceeded(state) {
      state.flags.isRequestingChangePassword = false;
    },
    receiveChangePasswordFailed(state, action: PayloadAction<string>) {
      state.errors.changePasswordError = action.payload;
      state.flags.isRequestingChangePassword = false;
    },
    requestUserView(state) {
      state.flags.isRequestingUserView = true;
      state.errors.userViewError = undefined;
    },
    receiveUserViewSucceeded(
      state,
      action: PayloadAction<EMPLOYEE_VIEW_OPTIONS | undefined>,
    ) {
      state.flags.isRequestingUserView = false;
      state.currentView = action.payload ?? EMPLOYEE_VIEW_OPTIONS.CLIENT_MODE;
    },
    receiveUserViewFailed(state, action: PayloadAction<string>) {
      state.errors.userViewError = action.payload;
      state.flags.isRequestingUserView = false;
    },
    requestSetUserView(state) {
      state.flags.isRequestingSetUserView = true;
      state.errors.setUserViewError = undefined;
    },
    receiveSetUserViewSucceeded(
      state,
      action: PayloadAction<EMPLOYEE_VIEW_OPTIONS>,
    ) {
      state.flags.isRequestingSetUserView = false;
      state.currentView = action.payload;
    },
    receiveSetUserViewFailed(state, action: PayloadAction<string>) {
      state.errors.setUserViewError = action.payload;
      state.flags.isRequestingSetUserView = false;
    },
    requestUserElevation(state) {
      state.flags.isRequestingUserElevation = true;
      state.errors.userElevationError = undefined;
    },
    receiveUserElevationSucceeded(
      state,
      action: PayloadAction<IGetElevationResponse>,
    ) {
      const { expirationTimestamp, isElevated } = action.payload;

      state.flags.isRequestingUserElevation = false;
      state.isElevated = isElevated;
      state.elevationExpirationTimestamp = expirationTimestamp;
    },
    receiveUserElevationFailed(state, action: PayloadAction<string>) {
      state.errors.userElevationError = action.payload;
      state.flags.isRequestingUserElevation = false;
    },
    requestRevokeUserElevation(state) {
      state.flags.isRequestingRevokeUserElevation = true;
      state.errors.revokeUserElevationError = undefined;
    },
    receiveRevokeUserElevationSucceeded(state) {
      state.flags.isRequestingRevokeUserElevation = false;
      state.isElevated = false;
      state.elevationExpirationTimestamp = undefined;
    },
    receiveRevokeUserElevationFailed(state, action: PayloadAction<string>) {
      state.errors.revokeUserElevationError = action.payload;
      state.flags.isRequestingRevokeUserElevation = false;
    },
    requestElevateUser(state) {
      state.flags.isRequestingElevateUser = true;
      state.errors.elevateUserError = undefined;
    },
    receiveElevateUserSucceeded(
      state,
      action: PayloadAction<IGetElevationResponse>,
    ) {
      state.flags.isRequestingElevateUser = false;
      state.isElevated = action.payload.isElevated;
      state.elevationExpirationTimestamp = action.payload.expirationTimestamp;
    },
    receiveElevateUserFailed(state, action: PayloadAction<string>) {
      state.errors.elevateUserError = action.payload;
      state.flags.isRequestingElevateUser = false;
    },
  },
});

export const fetchTimezones = createThunk(() => async (dispatch) => {
  dispatch(actions.requestTimezones());
  try {
    const timezones = await accountSettingsApi.getTimezones();
    dispatch(actions.receiveTimezonesSucceeded(timezones));
  } catch (_e) {
    dispatch(
      actions.receiveTimezonesFailed(
        'An error occurred when trying to fetch time zones.',
      ),
    );
  }
});

export const setTimezone = createThunk(
  (timeZoneId: TimeZoneId) => async (dispatch) => {
    dispatch(actions.requestSetTimezone());
    try {
      await accountSettingsApi.updateTimezone(timeZoneId);
      dispatch(actions.receiveSetTimezoneSucceeded());
      recordEvent(
        TRACKING_CATEGORY.CATEGORY_SETTINGS,
        TRACKING_ACTION.ACTION_GENERAL_UPDATE_TIMEZONE,
        TRACKING_LABEL.SUCCESS,
      );
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Time zone successfully updated!',
          type: ToastType.SUCCESS,
        }),
      );
      setTimeout(() => {
        window.location.reload();
        // Wait a brief period so the user can see the success toast.
      }, 2000);
    } catch (_e) {
      dispatch(
        actions.receiveSetTimezoneFailed(
          'An error occurred when trying to update your time zone.',
        ),
      );
      recordEvent(
        TRACKING_CATEGORY.CATEGORY_SETTINGS,
        TRACKING_ACTION.ACTION_GENERAL_UPDATE_TIMEZONE,
        TRACKING_LABEL.FAIL,
      );
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'An error occurred when trying to update your time zone.',
          type: ToastType.ERROR,
        }),
      );
    }
  },
);

export const fetchUserAuthType = createThunk(
  (email: string) => async (dispatch) => {
    dispatch(actions.requestUserAuthType());
    try {
      const authType =
        await gatewayAuthenticationApi.getUserAuthTypeByEmail(email);
      dispatch(actions.receiveUserAuthTypeSucceeded(authType));
    } catch (_e) {
      dispatch(
        actions.receiveUserAuthTypeFailed(
          'An error occurred when trying to fetch user auth type.',
        ),
      );
    }
  },
);

export type PASSWORD_CHANGE_STATUS = 'success' | 'password-mismatch' | 'error';
export const changePassword = createThunk(
  (
    currentPassword: string,
    newPassword: string,
    newPasswordConfirmation: string,
  ) =>
    async (dispatch): Promise<PASSWORD_CHANGE_STATUS> => {
      dispatch(actions.requestChangePassword());
      try {
        await accountSettingsApi.changeUserPassword(
          currentPassword,
          newPassword,
          newPasswordConfirmation,
        );
        dispatch(actions.receiveChangePasswordSucceeded());
        recordEvent(
          TRACKING_CATEGORY.CATEGORY_SETTINGS,
          TRACKING_ACTION.ACTION_GENERAL_PASSWORD_CHANGE,
          TRACKING_LABEL.SUCCESS,
        );
        dispatch(
          toastNotificationsActions.addToastNotification({
            title: 'Your password has been updated!',
            message: 'Now redirecting you to login.',
            type: ToastType.SUCCESS,
          }),
        );
        setTimeout(() => {
          // Globally sign out the user (across all devices)
          // so that they have to log in with the new password.
          Auth.signOut({ global: true });
          // Wait a brief period so the user can see the success toast.
        }, 2000);
        return 'success';
      } catch (e) {
        dispatch(
          actions.receiveChangePasswordFailed('Failed to change password.'),
        );
        recordEvent(
          TRACKING_CATEGORY.CATEGORY_SETTINGS,
          TRACKING_ACTION.ACTION_GENERAL_PASSWORD_CHANGE,
          TRACKING_LABEL.FAIL,
        );
        if (getRequestErrorMessage(e) === 'Old password does not match.') {
          return 'password-mismatch';
        } else {
          return 'error';
        }
      }
    },
);

export const fetchUserView = createThunk(() => async (dispatch) => {
  dispatch(actions.requestUserView());
  try {
    const response = await accountSettingsApi.getInternalView();
    dispatch(actions.receiveUserViewSucceeded(response.view));
  } catch (_e) {
    dispatch(
      actions.receiveUserViewFailed(
        'An error occurred when trying to fetch feature view.',
      ),
    );
  }
});

export const setUserView = createThunk(
  (view: EMPLOYEE_VIEW_OPTIONS) => async (dispatch) => {
    dispatch(actions.requestSetUserView());
    try {
      await accountSettingsApi.setInternalView(view);
      recordEvent(
        TRACKING_CATEGORY.CATEGORY_SETTINGS,
        getTrackingActionForEmployeeView(view),
        TRACKING_LABEL.SUCCESS,
      );
      dispatch(actions.receiveSetUserViewSucceeded(view));
      window.location.reload();
    } catch (_e) {
      recordEvent(
        TRACKING_CATEGORY.CATEGORY_SETTINGS,
        getTrackingActionForEmployeeView(view),
        TRACKING_LABEL.FAIL,
      );
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'An error occurred when trying to update feature view.',
          type: ToastType.ERROR,
        }),
      );
      dispatch(
        actions.receiveSetUserViewFailed(
          'An error occurred when trying to update feature view.',
        ),
      );
    }
  },
);

export const fetchUserElevation = createThunk(() => async (dispatch) => {
  dispatch(actions.requestUserElevation());
  try {
    const response = await accountSettingsApi.getElevation();
    dispatch(actions.receiveUserElevationSucceeded(response));
  } catch (e) {
    dispatch(
      actions.receiveUserElevationFailed(
        'An error occurred when trying to fetch user elevation.',
      ),
    );
  }
});

export const revokeUserElevation = createThunk(() => async (dispatch) => {
  dispatch(actions.requestRevokeUserElevation());
  try {
    await accountSettingsApi.revokeUserElevation();
    recordEvent(
      TRACKING_CATEGORY.CATEGORY_SETTINGS,
      TRACKING_ACTION.ACTION_EMPLOYEE_ELEVATION_DISABLED,
      TRACKING_LABEL.SUCCESS,
    );
    dispatch(actions.receiveRevokeUserElevationSucceeded());
    window.location.reload();
  } catch (_e) {
    recordEvent(
      TRACKING_CATEGORY.CATEGORY_SETTINGS,
      TRACKING_ACTION.ACTION_EMPLOYEE_ELEVATION_DISABLED,
      TRACKING_LABEL.FAIL,
    );
    dispatch(
      toastNotificationsActions.addToastNotification({
        title: 'An error occurred when trying to revoke user elevation.',
        type: ToastType.ERROR,
      }),
    );
    dispatch(
      actions.receiveRevokeUserElevationFailed(
        'An error occurred when trying to revoke user elevation.',
      ),
    );
  }
});

export const elevateUser = createThunk(
  (reason: string, otherReason: string) => async (dispatch) => {
    dispatch(actions.requestElevateUser());
    try {
      const response = await accountSettingsApi.elevateUser(
        reason,
        otherReason,
      );
      recordEvent(
        TRACKING_CATEGORY.CATEGORY_SETTINGS,
        TRACKING_ACTION.ACTION_EMPLOYEE_ELEVATION_ENABLED,
        TRACKING_LABEL.SUCCESS,
      );
      dispatch(actions.receiveElevateUserSucceeded(response));
      window.location.reload();
    } catch (_e) {
      recordEvent(
        TRACKING_CATEGORY.CATEGORY_SETTINGS,
        TRACKING_ACTION.ACTION_EMPLOYEE_ELEVATION_ENABLED,
        TRACKING_LABEL.FAIL,
      );
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'An error occurred when trying to elevate user.',
          type: ToastType.ERROR,
        }),
      );
      dispatch(
        actions.receiveElevateUserFailed(
          'An error occurred when trying to elevate user.',
        ),
      );
    }
  },
);

export default reducer;
